/**
 * Peptide reconstitution draw calculator.
 *
 *   vialTotal (in the same unit as the dose) divided across `bacMl` mL gives concentration.
 *   mlNeeded = dose / (vialTotal / bacMl)
 *   units    = mlNeeded * (syringe.units / syringe.ml)
 *
 * Returns null for incomplete input, or { mismatch: true } if vial↔dose units can't be safely converted.
 */
export function computeDraw({ vialValue, vialUnit, bacMl, doseValue, doseUnit, syringe }) {
  const v = parseFloat(vialValue);
  const b = parseFloat(bacMl);
  const d = parseFloat(doseValue);
  if (!v || !b || !d) return null;

  // Convert vial total into the dose's unit so we can divide.
  let vialInDoseUnit = null;
  let mismatch = false;

  if (vialUnit === doseUnit) {
    vialInDoseUnit = v;
  } else if (vialUnit === "mg" && doseUnit === "mcg") {
    vialInDoseUnit = v * 1000;
  } else if (vialUnit === "mcg" && doseUnit === "mg") {
    vialInDoseUnit = v / 1000;
  } else {
    // mg ↔ IU is peptide-specific; refuse silently to avoid wrong math.
    mismatch = true;
  }

  if (mismatch || !vialInDoseUnit) return { mismatch: true };

  const concentrationPerMl = vialInDoseUnit / b;
  const mlNeeded = d / concentrationPerMl;
  const unitsNeeded = mlNeeded * (syringe.units / syringe.ml);
  const dosesPerVial = vialInDoseUnit / d;

  return {
    concentrationPerMl,
    mlNeeded,
    unitsNeeded,
    dosesPerVial,
    overflow: unitsNeeded > syringe.units,
    vialInDoseUnit,
  };
}
